import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCategories } from "@/features/marketplace/hooks/useCategories";
import type { ProductCondition } from "@/features/marketplace/types/product";
import type { SearchFilters } from "@/features/marketplace/types/search";
import { cn } from "@/utils/cn";

const CONDITIONS: Array<{ value: ProductCondition; label: string }> = [
  { value: "new", label: "New" },
  { value: "like-new", label: "Like New" },
  { value: "used", label: "Used" },
  { value: "for-parts", label: "For Parts" },
];

interface FilterSidebarProps {
  filters: SearchFilters;
  onChange: (partial: Partial<SearchFilters>) => void;
  onClear: () => void;
}

/**
 * docs/ui/design-system.md §11 Filters: category, price range, condition —
 * the same filter model rendered in two containers (a persistent sidebar
 * on desktop, this same component inside a `Drawer`/`BottomSheet` on
 * mobile — see SearchResultsPage). One implementation, never two that can
 * drift out of sync.
 */
export function FilterSidebar({ filters, onChange, onClear }: FilterSidebarProps) {
  const categoriesQuery = useCategories();
  const hasActiveFilters =
    Boolean(filters.category) ||
    filters.minPrice != null ||
    filters.maxPrice != null ||
    filters.conditions.length > 0;

  function toggleCondition(condition: ProductCondition, checked: boolean) {
    const next = checked
      ? [...filters.conditions, condition]
      : filters.conditions.filter((value) => value !== condition);
    onChange({ conditions: next });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-h4 text-foreground">Filters</h3>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClear}
            className="text-body-sm text-primary underline-offset-4 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="space-y-2">
        <Label>Category</Label>
        <div className="space-y-1.5">
          <button
            type="button"
            onClick={() => onChange({ category: null })}
            className={cn(
              "block text-body-sm",
              !filters.category
                ? "font-medium text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            All categories
          </button>
          {categoriesQuery.data?.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => onChange({ category: category.slug })}
              className={cn(
                "block text-body-sm",
                filters.category === category.slug
                  ? "font-medium text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="minPrice">Price range</Label>
        <div className="flex items-center gap-2">
          <Input
            id="minPrice"
            type="number"
            inputMode="numeric"
            placeholder="Min"
            value={filters.minPrice ?? ""}
            onChange={(event) =>
              onChange({ minPrice: event.target.value ? Number(event.target.value) : null })
            }
          />
          <span className="text-muted-foreground" aria-hidden="true">
            –
          </span>
          <Input
            aria-label="Max price"
            type="number"
            inputMode="numeric"
            placeholder="Max"
            value={filters.maxPrice ?? ""}
            onChange={(event) =>
              onChange({ maxPrice: event.target.value ? Number(event.target.value) : null })
            }
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Condition</Label>
        <div className="space-y-1.5">
          {CONDITIONS.map(({ value, label }) => (
            <div key={value} className="flex items-center gap-2">
              <Checkbox
                id={`condition-${value}`}
                checked={filters.conditions.includes(value)}
                onCheckedChange={(checked) => toggleCondition(value, checked === true)}
              />
              <Label htmlFor={`condition-${value}`} className="font-normal">
                {label}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
